import React from 'react';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { fetchLocationInsightSubLandingPageData } from '../../common/helpers/data/landingPages/fetchLocationInsightLandingPageData';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import {
  LandingPageTypes,
  LocationInsightSubLandingPageType,
} from '../../common/helpers/domain/landingPages';
import { PostSubcategory } from '../../common/helpers/domain/postCategories';
import logger from '../../common/helpers/logger/logger';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getNoTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generateLocationInsightSubLandingPageBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import {
  HTTP_RESPONSE_CODES,
  TrainlinePostTag,
  ZonePostTag,
} from '../../common/helpers/constants';
import { parseLocationInsightSubLandingPageType } from '../../common/helpers/routing/landingPageUrlParser';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackHeroVideoArticle,
  trackMostPopularArticle,
  trackRecommendedLocationCard,
  trackRecommendedLocationCarouselArrow,
} from '../../common/analytics/analyticsTracker';
import { WholeState } from '../../store/common/type';
import { useSelector } from 'react-redux';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface Props {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  latestPosts: Content[];
  recommendedLocations: RecommendedLocation[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
  landingPageType: LocationInsightSubLandingPageType;
}

function LocationInsightSubLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  latestPosts,
  recommendedLocations,
  heroVideo,
  featuredVideos,
  landingPageType,
}: Props) {
  const moduleResource = commonResource.postModules.locationInsight;
  const analyticsSource = generateEventSourceByPageType(landingPageType);
  const isTrainlinePage = landingPageType === LandingPageTypes.TrainlinesPage;
  const sect = getAdSectByPageType(LandingPageTypes.LocationInsightPage);
  const subsect = isTrainlinePage
    ? getAdSectByPageType(LandingPageTypes.TrainlinesPage)
    : getAdSectByPageType(LandingPageTypes.ZonesPage);
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb
        items={generateLocationInsightSubLandingPageBreadcrumbList(
          landingPageType,
        )}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={
            isTrainlinePage
              ? homeResource.title.trainlines
              : homeResource.title.zones
          }
          subtitle={
            isTrainlinePage
              ? homeResource.subtitle.trainlines
              : homeResource.subtitle.zones
          }
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(post) =>
                trackMostPopularArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={sect}
          subsect={subsect}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      {recommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={
              isTrainlinePage
                ? homeResource.locationRecommendation.trainlines.title
                : homeResource.locationRecommendation.zones.title
            }
            subtitle={
              isTrainlinePage
                ? homeResource.locationRecommendation.trainlines.subtitle
                : homeResource.locationRecommendation.zones.subtitle
            }
          />
          <DynamicComponentForLocationRecommendation
            locations={recommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                isTrainlinePage
                  ? ANALYTICS_FEATURES.LOCATION_TRAINELINE_HIGHLIGHT
                  : ANALYTICS_FEATURES.LOCATION_ZONE_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                isTrainlinePage
                  ? ANALYTICS_FEATURES.LOCATION_TRAINELINE_HIGHLIGHT
                  : ANALYTICS_FEATURES.LOCATION_ZONE_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={sect}
          subsect={subsect}
        />
      </Row>
      {latestPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="location-insight"
        >
          <ModuleTitle
            title={
              isTrainlinePage
                ? moduleResource.trainlines.title
                : moduleResource.zones.title
            }
            subtitle={
              isTrainlinePage
                ? moduleResource.trainlines.subtitle
                : moduleResource.zones.subtitle
            }
          />
          <PostList
            contents={latestPosts}
            adUnit={AdUnits.HP_Pos1}
            adSect={sect}
            adSubsect={subsect}
            onClickPostCard={(post) =>
              trackArticle(
                userInfo,
                post,
                analyticsSource,
                isTrainlinePage
                  ? ANALYTICS_FEATURES.LATEST_LOCATION_TRAINELINE
                  : ANALYTICS_FEATURES.LATEST_LOCATION_ZONE,
              )
            }
          />
        </Row>
      )}
      {(heroVideo || featuredVideos.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideo}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideos}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard3}
          sect={sect}
          subsect={subsect}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getNoTabDefinition(
            null,
            [PostSubcategory.LocationInsight],
            getPageAssociatePostTag(landingPageType),
          )}
          excludePostIds={extractPostIds(
            [...latestPosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={sect}
          adSubsect={subsect}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

LocationInsightSubLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
  res,
  query,
}: CustomNextPageContext): Promise<Props> => {
  const landingPageType = parseLocationInsightSubLandingPageType(
    query.param as string,
  );
  if (!landingPageType) {
    res.redirect(HTTP_RESPONSE_CODES.TemporarilyMoved, '/');
  }

  const canonicalLink = config.baseUrl + asPath.substring(1);
  const associatePostTag = getPageAssociatePostTag(landingPageType);
  try {
    const { heroArticle, mostPopular, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, {
        postCategories: [PostSubcategory.LocationInsight],
        tag: associatePostTag,
      });

    const { locationInsight, video } =
      await fetchLocationInsightSubLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
        associatePostTag,
      );

    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    const filteredRecommendedLocations = recommendedLocations.filter((item) => {
      if (landingPageType === LandingPageTypes.TrainlinesPage) {
        return item.locationType === 'metro_station';
      } else if (landingPageType === LandingPageTypes.ZonesPage)
        if (
          item.locationType === 'zone' ||
          item.locationType === 'district' ||
          item.locationType === 'road'
        ) {
          return item;
        }
    });

    return {
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      latestPosts: locationInsight,
      recommendedLocations: filteredRecommendedLocations,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
      landingPageType,
    };
  } catch (e) {
    logger.logError(
      'LocationInsightSubLandingPage.getInitialProps',
      'Error fetching data',
      e,
    );
  }
};

function getPageAssociatePostTag(
  landingPageType: LocationInsightSubLandingPageType,
): string {
  return landingPageType === LandingPageTypes.ZonesPage
    ? ZonePostTag
    : TrainlinePostTag;
}

export default LocationInsightSubLandingPage;
